/**
 * BulkLeaseRenewal — landlord Dashboard widget that lets an operator
 * pick 20+ active leases and renew them all in one call. Renders a
 * button on the dashboard which opens an inline drawer with a
 * multi-select lease table + extend-months / rent-increase-% controls.
 *
 * Backend contract:
 *   POST /api/leases/bulk-renew
 *   body: { lease_ids: [], extend_months: 12, rent_increase_pct: 0 }
 *   → { ok, renewed, skipped, results: [{lease_id, status, ...}] }
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, CalendarClock, CheckCircle2, X, TrendingUp, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/friendlyError";

const money = (cents) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format((Number(cents) || 0) / 100);

export default function BulkLeaseRenewal() {
  const [open, setOpen] = useState(false);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [extendMonths, setExtendMonths] = useState(12);
  const [rentPct, setRentPct] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [lastResults, setLastResults] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/leases");
      const rows = Array.isArray(r.data) ? r.data : r.data?.leases || [];
      // Only renewable rows — active or expiring, never terminated / cancelled.
      const renewable = rows.filter((l) => !["terminated", "cancelled"].includes((l.status || "").toLowerCase()));
      setLeases(renewable);
    } catch (e) {
      toast.error(friendlyError(e, "Could not load leases"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(leases.map((l) => l.lease_id)));
  const clearAll = () => setSelected(new Set());

  const renewSelected = async () => {
    const ids = [...selected];
    if (!ids.length) {
      toast.error("Select at least one lease to renew");
      return;
    }
    setSubmitting(true);
    setLastResults(null);
    try {
      const r = await api.post("/leases/bulk-renew", {
        lease_ids: ids,
        extend_months: Number(extendMonths) || 12,
        rent_increase_pct: Number(rentPct) || 0,
      });
      setLastResults(r.data);
      const { renewed, skipped } = r.data || {};
      toast.success(`Renewed ${renewed} lease${renewed === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped` : ""}`);
      setSelected(new Set());
      load(); // refresh table with updated end dates
    } catch (e) {
      toast.error(friendlyError(e, "Bulk renewal failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selected.size;
  // Estimated total monthly rent uplift so landlord sees the impact before
  // clicking the big green button.
  const projectedUplift = useMemo(() => {
    const pct = Number(rentPct) || 0;
    if (!pct) return 0;
    return leases
      .filter((l) => selected.has(l.lease_id))
      .reduce((sum, l) => sum + Math.round((Number(l.monthly_rent) || 0) * pct / 100), 0);
  }, [selected, leases, rentPct]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="bulk-renew-btn"
        className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border border-slate-200 hover:border-emerald-300 card-lift group text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CalendarClock className="w-5 h-5"/>
          </div>
          <div>
            <div className="font-display text-base font-bold text-slate-900">Bulk lease renewal</div>
            <div className="text-xs text-slate-500 mt-0.5">Renew up to 200 tenants in one click — with optional rent increase.</div>
          </div>
        </div>
        <span className="hidden sm:inline text-xs font-bold uppercase tracking-widest text-emerald-700 opacity-0 group-hover:opacity-100 transition">Open →</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="bulk-renew-modal">
          <div className="bg-white w-full sm:max-w-4xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-slate-200 flex-none">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
                  <CalendarClock className="w-3 h-3"/> Bulk renew
                </div>
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-slate-900 mt-2">Renew selected leases</h2>
                <p className="text-sm text-slate-500 mt-0.5">Pick up to 200 leases, choose how many months to extend, and (optionally) a rent uplift.</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="bulk-renew-close"
                className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
                aria-label="Close"
              >
                <X className="w-5 h-5"/>
              </button>
            </div>

            {/* Controls */}
            <div className="p-5 border-b border-slate-200 grid sm:grid-cols-2 gap-4 flex-none bg-slate-50">
              <div>
                <label className="text-xs font-semibold text-slate-600">Extend by (months)</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={extendMonths}
                  onChange={(e) => setExtendMonths(e.target.value)}
                  data-testid="bulk-renew-months"
                  inputMode="numeric"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm tabular-nums focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                />
                <div className="text-xs text-slate-500 mt-1">1 to 60 months.</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Rent increase (%)</label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  step={0.1}
                  value={rentPct}
                  onChange={(e) => setRentPct(e.target.value)}
                  data-testid="bulk-renew-pct"
                  inputMode="decimal"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm tabular-nums focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Max 10% per bulk action. Provincial rent-increase notice periods (e.g. Ontario N1: 90 days) still apply.
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin"/> Loading leases…
                </div>
              ) : leases.length === 0 ? (
                <div className="p-10 text-center text-slate-500 text-sm">
                  You don&apos;t have any renewable leases yet. Create a lease first, then come back.
                </div>
              ) : (
                <div>
                  <div className="sticky top-0 z-10 flex items-center justify-between bg-white border-b border-slate-100 px-5 py-2 text-xs text-slate-600">
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={selectAll} data-testid="bulk-renew-select-all" className="font-semibold text-emerald-700 hover:underline">Select all</button>
                      <button type="button" onClick={clearAll} data-testid="bulk-renew-clear-all" className="font-semibold text-slate-500 hover:underline">Clear</button>
                    </div>
                    <div className="flex items-center gap-1 tabular-nums font-semibold text-slate-800">
                      <Users2 className="w-3.5 h-3.5"/> {selectedCount} / {leases.length} selected
                    </div>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {leases.map((l) => {
                      const isSelected = selected.has(l.lease_id);
                      return (
                        <li key={l.lease_id}>
                          <label className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${isSelected ? "bg-emerald-50" : "hover:bg-slate-50"}`} data-testid={`bulk-renew-row-${l.lease_id}`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggle(l.lease_id)}
                              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                              data-testid={`bulk-renew-checkbox-${l.lease_id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-900 text-sm truncate">{l.tenant_name || l.tenant_email || "Unnamed tenant"}</div>
                              <div className="text-xs text-slate-500 truncate">Lease {l.lease_id?.slice(0, 8)} · {l.status || "active"}</div>
                            </div>
                            <div className="text-right text-xs tabular-nums flex-none">
                              <div className="text-slate-500">Ends</div>
                              <div className="font-semibold text-slate-900">{l.end_date || "—"}</div>
                            </div>
                            <div className="text-right text-xs tabular-nums flex-none w-20">
                              <div className="text-slate-500">Rent</div>
                              <div className="font-semibold text-slate-900">{money(l.monthly_rent)}</div>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 p-5 bg-white flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between flex-none">
              <div className="text-xs text-slate-600">
                <div className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-emerald-600"/> Projected new monthly income uplift: <span className="font-bold text-slate-900 tabular-nums">{money(projectedUplift)}</span></div>
                {lastResults && (
                  <div className="mt-1 text-emerald-700 font-semibold flex items-center gap-1" data-testid="bulk-renew-result">
                    <CheckCircle2 className="w-3.5 h-3.5"/>
                    Last run: {lastResults.renewed} renewed, {lastResults.skipped} skipped.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={renewSelected}
                disabled={submitting || selectedCount === 0}
                data-testid="bulk-renew-submit"
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold w-full sm:w-auto justify-center ${selectedCount > 0 && !submitting ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}
              >
                {submitting ? (<><RefreshCw className="w-4 h-4 animate-spin"/> Renewing…</>) : (<><CalendarClock className="w-4 h-4"/> Renew {selectedCount || ""} selected</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
